import {
  type IntelReport,
  type ProcessedIntelReportFilters,
  processIntelReportFilters,
} from '@pn/core/domain/intel/intel-report';
import {
  buildIntelQueryObject,
  createIntelWorkspaceItem,
  type IntelWorkspaceItem,
} from '@pn/core/domain/intel/intel-workspace-item';
import type { Filter } from '@pn/core/domain/query';
import { isEmpty, isNil } from 'lodash-es';
import { generateWellMismatches } from './report-defaults/wellMismatches';

export type ReportFiltersAndId = ProcessedIntelReportFilters & {
  linkedReportId: string;
};

export function generateIntelQueriesForReport(
  report: IntelReport,
  companyBrandNameRecords: Record<string, string>,
  landSaleDate: string
): IntelWorkspaceItem[] {
  const processedFilters: ReportFiltersAndId = {
    ...processIntelReportFilters(report.filters, companyBrandNameRecords),
    linkedReportId: report.id,
  };

  const { startDate, endDate, geoJSONGeometry } = processedFilters;

  const intelQueries: IntelWorkspaceItem[] = [
    ...generateWellQueries(processedFilters),
    ...generateAERQueries(processedFilters),
    ...generateMRTransfersQueries(processedFilters),
    ...generateLicenceTransfersQueries(processedFilters),
    ...generateWellMismatches(processedFilters),
  ];

  if (report.filters.type === 'Area') {
    // land is only possible for area reports (for now)
    const newLandPostingsQuery = createIntelWorkspaceItem({
      query: buildIntelQueryObject('land_postings', {
        filters: [
          {
            field: 'notice_date',
            operator: 'isAnyOf',
            value: landSaleDate,
          },
          {
            field: 'geom',
            operator: 'intersects',
            value: geoJSONGeometry,
          },
        ],
      }),
      linkedReportId: report.id,
    });
    intelQueries.push(newLandPostingsQuery);
    const newLandResultsQuery = createIntelWorkspaceItem({
      query: buildIntelQueryObject('land_results', {
        filters: [
          {
            field: 'notice_date',
            operator: '>=',
            value: startDate,
          },
          {
            field: 'notice_date',
            operator: '<=',
            value: endDate,
          },
          {
            field: 'geom',
            operator: 'intersects',
            value: geoJSONGeometry,
          },
        ],
      }),
      linkedReportId: report.id,
    });
    intelQueries.push(newLandResultsQuery);
  }

  if (
    report.filters.type === 'Company' &&
    !isNil(processedFilters.companyBrandName)
  ) {
    const newLandResultsQuery = createIntelWorkspaceItem({
      query: buildIntelQueryObject('crown_mineral_activity', {
        filters: [
          {
            field: 'approval_date',
            operator: 'onOrAfter',
            value: startDate,
          },
          {
            field: 'approval_date',
            operator: 'onOrBefore',
            value: endDate,
          },
          {
            field: 'designated_representative',
            operator: 'contains',
            value: processedFilters.companyBrandName,
          },
        ],
      }),
      linkedReportId: report.id,
    });
    intelQueries.push(newLandResultsQuery);
  }
  if (
    report.filters.type === 'Area' &&
    !isNil(processedFilters.geoJSONGeometry)
  ) {
    const newLandResultsQuery = createIntelWorkspaceItem({
      query: buildIntelQueryObject('crown_mineral_activity', {
        filters: [
          {
            field: 'approval_date',
            operator: 'onOrAfter',
            value: startDate,
          },
          {
            field: 'approval_date',
            operator: 'onOrBefore',
            value: endDate,
          },
        ],
        geometry: processedFilters.geoJSONGeometry,
      }),
      linkedReportId: report.id,
    });
    intelQueries.push(newLandResultsQuery);
  }
  return intelQueries;
}

function generateLicenceTransfersQueries({
  startDate,
  endDate,
  companyBrandName,
  formation,
  fieldName,
  geoJSONGeometry,
  linkedReportId,
}: ReportFiltersAndId) {
  const baseLicenceTransfersFilters: Filter[] = [
    {
      field: 'transfer_date',
      operator: '>=',
      value: startDate,
    },
    {
      field: 'transfer_date',
      operator: '<=',
      value: endDate,
    },
  ];

  if (!isNil(geoJSONGeometry)) {
    baseLicenceTransfersFilters.push({
      field: 'geom',
      operator: 'intersects',
      value: geoJSONGeometry,
    });
  }

  if (!isEmpty(formation) || !isEmpty(fieldName)) return []; // no licence transfers fields/formations

  if (isEmpty(companyBrandName)) {
    return [
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('well_license_transfers', {
          filters: baseLicenceTransfersFilters,
        }),
        linkedReportId,
      }),
    ];
  } else {
    return [
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('well_license_transfers', {
          filters: [
            ...baseLicenceTransfersFilters,
            {
              field: 'from_company_brand.display_name',
              operator: '=',
              value: companyBrandName,
            },
          ],
        }),
        name: 'Licence Transfer From ' + companyBrandName,
        linkedReportId,
      }),
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('well_license_transfers', {
          filters: [
            ...baseLicenceTransfersFilters,
            {
              field: 'to_company_brand.display_name',
              operator: '=',
              value: companyBrandName,
            },
          ],
        }),
        name: 'Licence Transfer To ' + companyBrandName,
        linkedReportId,
      }),
    ];
  }
}

function generateMRTransfersQueries({
  startDate,
  endDate,
  companyBrandName,
  formation,
  fieldName,
  geoJSONGeometry,
  linkedReportId,
}: ReportFiltersAndId) {
  const baseMRTransfersFilters: Filter[] = [
    {
      field: 'created_at',
      operator: '>=',
      value: startDate,
    },
    {
      field: 'created_at',
      operator: '<=',
      value: endDate,
    },
  ];

  if (!isNil(geoJSONGeometry)) {
    baseMRTransfersFilters.push({
      field: 'geom',
      operator: 'intersects',
      value: geoJSONGeometry,
    });
  }

  if (!isEmpty(formation) || !isEmpty(fieldName)) return []; // no MR transfers fields/formations
  if (isEmpty(companyBrandName)) {
    return [
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('mineral_rights_transfers', {
          filters: baseMRTransfersFilters,
        }),
        linkedReportId,
      }),
    ];
  } else {
    return [
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('mineral_rights_transfers', {
          filters: [
            ...baseMRTransfersFilters,
            {
              field: 'from_company_brand.display_name',
              operator: '=',
              value: companyBrandName,
            },
          ],
        }),
        name: 'MR Transfer From ' + companyBrandName,
        linkedReportId,
      }),
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('mineral_rights_transfers', {
          filters: [
            ...baseMRTransfersFilters,
            {
              field: 'to_company_brand.display_name',
              operator: '=',
              value: companyBrandName,
            },
          ],
        }),
        name: 'MR Transfer To ' + companyBrandName,
        linkedReportId,
      }),
    ];
  }
}

function generateAERQueries({
  startDate,
  endDate,
  formation,
  companyBrandName,
  fieldName,
  geoJSONGeometry,
  linkedReportId,
}: ReportFiltersAndId) {
  const baseAERTransfersFilters: Filter[] = [
    {
      field: 'last_status_date',
      operator: '>=',
      value: startDate,
    },
    {
      field: 'last_status_date',
      operator: '<=',
      value: endDate,
    },
  ];
  if (!isNil(geoJSONGeometry)) {
    baseAERTransfersFilters.push({
      field: 'geom',
      operator: 'intersects',
      value: geoJSONGeometry,
    });
  }

  if (!isEmpty(formation) || !isEmpty(fieldName)) return []; // no AER transfers fields/formations
  if (isEmpty(companyBrandName)) {
    return [
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('aer_transfers', {
          filters: baseAERTransfersFilters,
        }),
        linkedReportId,
      }),
    ];
  } else {
    return [
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('aer_transfers', {
          filters: [
            ...baseAERTransfersFilters,
            {
              field: 'from_company_brand.display_name',
              operator: 'isAnyOf',
              value: companyBrandName,
            },
          ],
        }),
        name: 'AER Transfer From ' + companyBrandName,
        linkedReportId,
      }),
      createIntelWorkspaceItem({
        query: buildIntelQueryObject('aer_transfers', {
          filters: [
            ...baseAERTransfersFilters,
            {
              field: 'to_company_brand.display_name',
              operator: 'isAnyOf',
              value: companyBrandName,
            },
          ],
        }),
        name: 'AER Transfer To ' + companyBrandName,
        linkedReportId,
      }),
    ];
  }
}

function generateWellQueries({
  startDate,
  endDate,
  formation,
  companyBrandName,
  fieldName,
  geoJSONGeometry,
  linkedReportId,
}: ReportFiltersAndId) {
  const wellLicencesFilters: Filter[] = [
    {
      field: 'licenses.license_date',
      operator: '>=',
      value: startDate,
    },
    {
      field: 'licenses.license_date',
      operator: '<=',
      value: endDate,
    },
  ];

  const wellSpudsFilters: Filter[] = [
    {
      field: 'last_spud_date',
      operator: '>=',
      value: startDate,
    },
    {
      field: 'last_spud_date',
      operator: '<=',
      value: endDate,
    },
  ];

  if (!isNil(geoJSONGeometry)) {
    wellLicencesFilters.push({
      field: 'wellbores.geom',
      operator: 'intersects',
      value: geoJSONGeometry,
    });

    wellSpudsFilters.push({
      field: 'wellbores.geom',
      operator: 'intersects',
      value: geoJSONGeometry,
    });
  }

  if (!isNil(formation)) {
    wellLicencesFilters.push({
      field: 'wellbores.formation',
      operator: '=',
      value: formation,
    });

    wellSpudsFilters.push({
      field: 'wellbores.formation',
      operator: '=',
      value: formation,
    });
  }
  if (!isNil(companyBrandName)) {
    wellLicencesFilters.push({
      field: 'company_brand.display_name',
      operator: '=',
      value: companyBrandName,
    });
    wellSpudsFilters.push({
      field: 'company_brand.display_name',
      operator: '=',
      value: companyBrandName,
    });
  }

  if (!isNil(fieldName)) {
    wellLicencesFilters.push({
      field: 'wellbores.field_name',
      operator: 'ILIKE',
      value: fieldName,
    });
    wellSpudsFilters.push({
      field: 'wellbores.field_name',
      operator: 'ILIKE',
      value: fieldName,
    });
  }

  return [
    createIntelWorkspaceItem({
      name: 'Licence Activity',
      query: buildIntelQueryObject('well_licenses', {
        filters: wellLicencesFilters,
      }),
      linkedReportId,
      hiddenFields: ['on_prod_date', 'rig_rlse_date', 'last_spud_date'],
    }),
    createIntelWorkspaceItem({
      name: 'Spud Activity',
      query: buildIntelQueryObject('well_licenses', {
        filters: wellSpudsFilters,
      }),
      linkedReportId,
      hiddenFields: ['on_prod_date', 'rig_rlse_date'],
    }),
  ];
}
